import React from "react";
import { Bot, HeartPulse, RotateCcw, Send, ShieldAlert, Sparkles, Stethoscope } from "lucide-react";
import { useTranslation } from "react-i18next";

import { UI_GPT_RESPONSE_CARD, UI_QUICK_ACTIONS } from "../../guided/constants/guidedUiTypes.js";

import "./AssistantIA.css";

const cleanText = (value = "") => String(value || "").trim();

const readLabels = (t, locale) => {
  const value = t("assistantIA", { lng: locale, returnObjects: true });
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  return t("assistantIA", { lng: "fr", returnObjects: true });
};

export default function AssistantIA({
  messages = [],
  loading = false,
  locale = "fr",
  onContinue,
}) {
  const { t } = useTranslation("assistantAi");
  const lang = ["fr", "en", "ar"].includes(locale) ? locale : "fr";
  const labels = readLabels(t, lang);
  const isRtl = lang === "ar";
  const [draft, setDraft] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const threadRef = React.useRef(null);

  const visibleMessages = messages.filter(
    (message) => message?.sender === "bot" && message?.ui?.type !== UI_QUICK_ACTIONS
  );
  const activeMessage = visibleMessages[visibleMessages.length - 1];
  const activeUi = activeMessage?.ui;
  const props = activeUi?.props || {};
  const isGptResponse = activeUi?.type === UI_GPT_RESPONSE_CARD;
  const flowId = props.flowId || activeUi?.flowId;
  const stepId = props.stepId || activeUi?.stepId;
  const title = cleanText(props.title) || labels.emptyTitle;
  const placeholder = props.followUpPlaceholder || labels.emptyText;
  const sendLabel = props.followUpLabel || labels.send;
  const loadingLabel = props.loadingLabel || labels.loading;
  const primaryAction = props.primaryAction;
  const disclaimer = props.showDisclaimer ? cleanText(props.disclaimer) : "";
  const conversation = React.useMemo(() => {
    if (Array.isArray(props.conversation) && props.conversation.length) {
      return props.conversation
        .map((item) => ({
          role: item?.role === "user" ? "user" : "assistant",
          content: cleanText(item?.content),
        }))
        .filter((item) => item.content);
    }
    if (cleanText(props.content || activeMessage?.text)) {
      return [{ role: "assistant", content: cleanText(props.content || activeMessage?.text) }];
    }
    return [];
  }, [activeMessage?.text, props.content, props.conversation]);

  React.useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [conversation.length, loading, submitting]);

  const busy = loading || submitting;

  const submitMessage = async () => {
    const message = draft.trim();
    if (!message || busy || !flowId) return;
    setSubmitting(true);
    try {
      await onContinue?.({ flowId, stepId }, { message });
      setDraft("");
    } finally {
      setSubmitting(false);
    }
  };

  const resetConversation = () => {
    if (!primaryAction || !flowId) return;
    onContinue?.({ flowId, stepId }, primaryAction.payload || {});
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitMessage();
    }
  };

  return (
    <div className="assistant-ia-page" dir={isRtl ? "rtl" : "ltr"}>
      <div className="assistant-ia-page__surface">
        <div className="assistant-ia-page__content">
          <section className="assistant-ia-shell" aria-label={title}>
            <header className="assistant-ia-topbar">
              <div className="assistant-ia-agent">
                <span className="assistant-ia-agent__icon" aria-hidden="true">
                  <HeartPulse size={24} />
                </span>
                <div className="assistant-ia-agent__copy">
                  <span className="assistant-ia-eyebrow">
                    <Sparkles size={13} />
                    {labels.eyebrow}
                  </span>
                  <h2>{title}</h2>
                  <p>{labels.subtitle}</p>
                </div>
              </div>
              {primaryAction && (
                <button
                  type="button"
                  className="assistant-ia-reset"
                  onClick={resetConversation}
                  title={primaryAction.label || labels.reset}
                  aria-label={primaryAction.label || labels.reset}
                >
                  <RotateCcw size={16} />
                </button>
              )}
            </header>

            <main className="assistant-ia-thread" ref={threadRef}>
              {conversation.length > 0 ? (
                conversation.map((item, index) => (
                  <div
                    className={`assistant-ia-message assistant-ia-message--${item.role}`}
                    key={`${item.role}-${index}`}
                  >
                    <span className="assistant-ia-message__avatar" aria-hidden="true">
                      {item.role === "user" ? <Stethoscope size={15} /> : <Bot size={15} />}
                    </span>
                    <div className="assistant-ia-message__bubble">{item.content}</div>
                  </div>
                ))
              ) : (
                <div className="assistant-ia-empty">
                  <div className="assistant-ia-empty__icon">AI</div>
                  <h2>{labels.emptyTitle}</h2>
                  <p>{labels.emptyText}</p>
                </div>
              )}

              {busy && (
                <div className="assistant-ia-message assistant-ia-message--assistant">
                  <span className="assistant-ia-message__avatar" aria-hidden="true">
                    <Bot size={15} />
                  </span>
                  <div className="assistant-ia-message__bubble assistant-ia-message__bubble--loading">
                    <span />
                    <span />
                    <span />
                    <p>{loadingLabel}</p>
                  </div>
                </div>
              )}
            </main>

            {disclaimer && (
              <aside className="assistant-ia-disclaimer">
                <ShieldAlert size={17} />
                <div>
                  <strong>{labels.disclaimerTitle}</strong>
                  <p>{disclaimer}</p>
                </div>
              </aside>
            )}

            <form
              className="assistant-ia-composer"
              onSubmit={(event) => {
                event.preventDefault();
                submitMessage();
              }}
            >
              <textarea
                value={draft}
                placeholder={placeholder}
                disabled={busy || !isGptResponse}
                rows={2}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button type="submit" disabled={!draft.trim() || busy || !isGptResponse}>
                <Send size={16} />
                <span>{busy ? loadingLabel : sendLabel}</span>
              </button>
            </form>

            {!isGptResponse && activeMessage?.text && (
              <div className="assistant-ia-plain-response">{activeMessage.text}</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
